(ns app.front.view.dashboard
  (:require [app.front.dashboard.business :as dashboard-business]
            [app.front.dashboard.code-enforcement :as dashboard-code-enforcement]
            [app.front.dashboard.contractor-registration :as dashboard-contractor-registration]
            [app.front.dashboard.group-configs :as dashboard-configs]
            [app.front.dashboard.inspections :as dashboard-inspections]
            [app.front.dashboard.licencing :as dashboard-licencing]
            [app.front.dashboard.meetings :as dashboard-meetings]
            [app.front.dashboard.my-workspaces :as my-workspaces]
            [app.front.dashboard.permit-number-search :as dashboard-permit-number-search]
            [app.front.dashboard.property-management :as dashboard-property-management]
            [app.front.dashboard.search :as dashboard-search]
            [app.front.dashboard.service-widget :as service-widget]
            [app.front.dashboard.welcome :as dashboard-welcome]
            [app.front.dashlets :as dashlets]
            [app.front.entities :as entities]
            [app.front.query.core :as query]
            [app.front.task :as task]
            [app.front.ui :as ui]
            [app.front.view.bond.list :as bond-list]
            [app.front.view.create-application.create-v2 :as create-v2]
            [app.front.view.escrow.account-list :as escrow-account-list]
            [app.front.view.search.dashboard-widget :as dashboard-search-widget]
            [app.front.view.search.saved-searches :as saved-searches]
            [app.front.view.search.subs :as search-subs]
            [app.front.view.workspace.search-mode :as workspace-search-mode]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.session :as session]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]))


(defn dashboard-heading-row [tr authority-or-admin?]
  [:div.bx--row.justify-between.mb-8.mt-4
   [:div.mt-4.mr-2
    [:h1 (tr [:my-dashboard])]]
   [:div.flex.flex-row-reverse.flex-wrap
    (when authority-or-admin?
      [dashboard-permit-number-search/open-workspace-button tr])]])


(rf/reg-event-fx ::queue-fetch-dashlet-meetings
  (fn [{:keys [db]} _]
    (if (:session/user db)
      {:dispatch [::fetch-dashlet-meetings]}
      {:async-flow {:rules [{:when     :seen-any-of?
                             :events   [::session/restored-session]
                             :dispatch [::fetch-dashlet-meetings]
                             :halt?    true}]}})))


(rf/reg-event-fx ::fetch-dashlet-meetings
  (fn [{:keys [db]} _]
    (when (-> db :session/user :user/authority?)
      {:comm/command {:command    :meeting/dashlet-meetings
                      :method     :get
                      :on-success [::entities/merge-entity-db]}})))


(rf/reg-sub ::authority-or-admin?
  :<- [:session/authority-user?]
  :<- [:session/admin?]
  (fn [[authority? admin?]]
    (or authority? admin?)))


(rf/reg-sub ::show-loading-spinner?
  :<- [::authority-or-admin?]
  :<- [::dashlets/dashlet-loading? task/default-task-dashlet-id]
  :<- [::dashboard-search/config-loading?]
  :<- [:comm/command-processing? :fee.escrow/get-my-accounts]
  :<- [:comm/command-processing? :business/my-businesses]
  :<- [:comm/command-processing? :bond/get-my-bonds]
  :<- [::ff/enabled? :feature-flag.id/bonds]
  (fn [[authority-user? tasks-loading? search-loading? escrow-loading? businesses-loading? bonds-loading? bonds-enabled?]]
    ;; We will never show the welcome page to authorities/admins, so do not show the spinner either
    (and (not authority-user?)
         (or tasks-loading?
             search-loading?
             escrow-loading?
             businesses-loading?
             (and bonds-enabled? bonds-loading?)))))


(rf/reg-sub ::escrow-accounts
  :<- [:session/user-id]
  (fn [owner-id]
    @(rf/subscribe [::escrow-account-list/accounts owner-id])))


(rf/reg-sub ::has-bonds?
  :<- [:session/user-id]
  :<- [::ff/enabled? :feature-flag.id/bonds]
  (fn [[owner-id bonds-enabled?] _]
    (boolean
      (and bonds-enabled?
           (seq (<sub [::bond-list/bonds owner-id]))))))


(rf/reg-sub ::show-welcome-page?
  :<- [::authority-or-admin?]
  :<- [::task/sorted-task-items task/default-task-dashlet-id]
  :<- [::dashboard-search/grand-total]
  :<- [::escrow-accounts]
  :<- [::has-bonds?]
  :<- [::dashboard-business/show-dashlet?]
  (fn [[authority-user? tasks ws-result-count accounts has-bonds? businesses?] _]
    (and (not authority-user?)
         (empty? tasks)
         ;; The count is nil while it is not known, and the welcome page is for users that are
         ;; known to have nothing.
         (= 0 ws-result-count)
         (empty? accounts)
         (not has-bonds?)
         (not businesses?))))


(defn view [match]
  (let [tr                  (<sub [:tr])
        loading?            (<sub [::show-loading-spinner?])
        inspector?          (<sub [:session/inspector-user?])
        dispatcher?         (<sub [:session/dispatcher-user?])
        admin?              (<sub [:session/admin?])
        authority-or-admin? (<sub [::authority-or-admin?])
        code-enforcement?   (<sub [:session/code-enforcement-authority?])
        work-orders?        (<sub [:session/work-orders-authority?])
        service-hub?        (ff/enabled? :feature-flag.id/service-hub-panel)]
    (if loading?
      (cc/loading-spinner {})
      (if (<sub [::show-welcome-page?])
        [dashboard-welcome/view tr]
        [:div.cloudpermit-page
         [:<>
          [ui/page-header (util/mmap match) [dashboard-heading-row tr authority-or-admin?]]
          [:div#dashboard
           [service-widget/service-widget tr]
           (when (<sub [::dashboard-business/show-dashlet?])
             [dashboard-business/businesses-dashlet])
           [escrow-account-list/account-list (util/mmap {:owner-entity-id (<sub [:session/user-id])} tr)]
           [bond-list/bond-list (util/mmap {:title (tr [:bond/my-bonds]) :owner-entity-id (<sub [:session/user-id])} tr)]
           [task/task-dashlet {:id task/default-task-dashlet-id}]
           (when (and (not service-hub?)
                      (<sub [::ff/enabled? :feature-flag.id/property-management])
                      (<sub [:session/property-management-visible?]))
             [dashboard-property-management/property-management-dashlet tr])
           (when (or inspector? dispatcher?)
             [dashboard-inspections/inspections-dashlet tr])
           (when (and (not service-hub?) (or code-enforcement? work-orders?))
             [dashboard-code-enforcement/dashboard-section tr code-enforcement? work-orders?])
           (when (and (not service-hub?) (<sub [::dashboard-licencing/show-dashlet?]))
             [dashboard-licencing/dashboard-section tr])
           (when (and (not service-hub?) (<sub [::dashboard-contractor-registration/show-dashlet?]))
             [dashboard-contractor-registration/dashboard-section tr])
           (when (<sub [::dashboard-meetings/show-meetings-dashlet])
             [dashboard-meetings/meetings-dashlet tr])
           (when admin?
             [dashboard-configs/section tr])
           (if (ff/enabled? :feature-flag.id/workspace-search-updates)
             (if authority-or-admin?
               [:<>
                [dashboard-search-widget/record-search-widget tr]
                [dashboard-search-widget/recently-viewed-widget tr]]
               [my-workspaces/dashboard-widget tr])
             [dashboard-search/workspace-search tr])]]]))))


(rf/reg-event-fx ::init-workspace-search
  (fn [_ _]
    ;; Run after session restoration so flags are known. The welcome page still needs the
    ;; config's grand total, but the new widgets do not need the legacy search results.
    {:dispatch [::dashboard-search/init workspace-search-mode/search-mode
                (if (ff/enabled? :feature-flag.id/workspace-search-updates)
                  {:search-event [:comm/noop]}
                  {})]}))

(def dashboard-view
  {:name        :dashboard
   :view        view
   :public?     false
   :controllers [{:start (fn [_]
                           (>evt [::query/fetch :committee/next-meetings])
                           (>evt [::queue-fetch-dashlet-meetings])
                           (>evt [::task/load-my-tasks task/default-task-dashlet-id nil])
                           (>evt [::dashboard-inspections/queue-fetch-bookings])
                           (>evt [::session/wait-for-user-data [::init-workspace-search]])
                           (>evt [::search-subs/fetch-domain-groups])
                           (>evt [::dashboard-code-enforcement/queue-fetch-open-complaint-total])
                           (>evt [::create-v2/close-create])
                           (>evt [::dashboard-licencing/queue-fetch-licence-count])
                           (>evt [::dashboard-contractor-registration/queue-fetch-registration-count])
                           (>evt [::dashboard-business/fetch-my-businesses])
                           (>evt [::escrow-account-list/fetch-my-accounts])
                           (>evt [::bond-list/fetch-my-bonds])
                           (>evt [::session/wait-for-user-data [::dashboard-search-widget/fetch-recently-viewed]])
                           (>evt [::session/wait-for-user-data [::my-workspaces/fetch]])
                           (>evt [::saved-searches/fetch-unified-filters])
                           (>evt [::saved-searches/fetch-filter-labels])
                           (>evt [::service-widget/init]))
                  :stop  (fn [_]
                           (>evt [::dashboard-permit-number-search/clear-search])
                           (>evt [::service-widget/cleanup]))}]})
